<template>
    <div class="calculators-view container mt-3 mb-5">
      <h1 class="mb-4 text-center">Calculateurs Médicaux</h1>

      
      <section class="calculator-select-section card card-body mb-4 shadow-sm sticky-top bg-light">
        <div class="row g-2 align-items-center">
          <div class="col-12 col-md-7">
            <label for="calculatorSelect" class="form-label visually-hidden">Choisir un Calculateur:</label>
            <select id="calculatorSelect" class="form-select" v-model="selectedCalculatorId">
              <option :value="null">-- Sélectionner un Calculateur --</option>
              <optgroup v-for="(group, category) in groupedCalculatorOptions" :key="category" :label="category">
                <option v-for="option in group" :key="option.id" :value="option.id">
                  {{ option.name }}
                </option>
              </optgroup>
            </select>
          </div>
          <div class="col-12 col-md-5">
             <label for="calculatorSearch" class="form-label visually-hidden">Rechercher:</label>
             <input type="search" id="calculatorSearch" class="form-control" v-model="searchTerm" placeholder="Rechercher par nom, catégorie, mot-clé...">
          </div>
        </div>
         <!-- Show reset button only if a calculator is selected -->
         <div v-if="selectedCalculator" class="text-end mt-2">
              <button @click="resetCalculatorSelection" class="btn btn-sm btn-link text-muted p-0">Changer de calculateur</button>
         </div>
      </section>

      <!-- Selected Calculator Display -->
      <section v-if="selectedCalculator" class="selected-calculator-section card shadow-sm">
         <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
             <h2 class="h5 mb-0">{{ selectedCalculator.name }}</h2>
             <button @click="resetSpecificCalculatorInputs" class="btn btn-sm btn-outline-light" title="Réinitialiser les valeurs de ce calculateur">
                <!-- SVG Icon remains the same -->
                <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512" fill="currentColor"><path d="M105.1 202.6c7.7-21.8 20.2-42.3 37.8-59.8c62.5-62.5 163.8-62.5 226.3 0L386.3 160H336c-17.7 0-32 14.3-32 32s14.3 32 32 32H463.5c0 0 0 0 0 0h.4c17.7 0 32-14.3 32-32V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v51.2L414.4 97.6c-87.5-87.5-229.3-87.5-316.8 0C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5zM39 289.3c-5 1.5-9.8 4.2-13.7 8.2c-4 4-6.7 8.8-8.1 14c-.3 1.2-.6 2.5-.8 3.8c-.3 1.7-.4 3.4-.4 5.1V448c0 17.7 14.3 32 32 32s32-14.3 32-32V398.8l14.4 14.4c87.5 87.5 229.3 87.5 316.8 0c24.4-24.4 42.1-53.1 52.9-83.7c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.5 62.5-163.8 62.5-226.3 0l-.7-.7L150.3 352H208c17.7 0 32-14.3 32-32s-14.3-32-32-32H80.4c-1.1 0-2.3 .1-3.4 .4c-1.2 .3-2.5 .6-3.8 .8c-5.1 1.4-9.9 4.1-14 8.1c-4 4-6.7 8.8-8.1 14c-.3 1.2-.6 2.5-.8 3.8c-.3 1.7-.4 3.4-.4 5.1z"/></svg>
                Reset
              </button>
         </div>
         <div class="card-body">
             <!-- Dynamic Form Generation -->
             <form @submit.prevent>
                  <div class="row g-3">
                      <div v-for="field in selectedCalculator.fields" :key="selectedCalculator.id + '-' + field.model" :class="field.colClass || 'col-12 col-md-6'">
                          <div class="form-group mb-3">

                              <!-- Number Input -->
                              <template v-if="field.type === 'number'">
                                  <label :for="`${selectedCalculator.id}-${field.model}`" class="form-label small mb-1">{{ field.label }}{{ field.unit ? ` (${field.unit})` : '' }}</label>
                                  <div class="input-group input-group-sm">
                                      <input
                                          type="number"
                                          :id="`${selectedCalculator.id}-${field.model}`"
                                          class="form-control"
                                          :step="field.step || 'any'"
                                          :min="field.min"
                                          :max="field.max"
                                          :placeholder="field.placeholder || ''"
                                          v-model.number="selectedCalculator.state[field.model]"
                                          :required="field.required !== false" 
                                    
                                      >
                                      <span v-if="field.unitDisplay" class="input-group-text">{{ field.unitDisplay }}</span>
                                  </div>
                              </template>

                              <!-- Select Dropdown -->
                              <template v-if="field.type === 'select'">
                                  <label :for="`${selectedCalculator.id}-${field.model}`" class="form-label small mb-1">{{ field.label }}</label>
                                  <select
                                      :id="`${selectedCalculator.id}-${field.model}`"
                                      class="form-select form-select-sm"
                                      v-model="selectedCalculator.state[field.model]"
                                      :required="field.required !== false"
                                  >
                                      <option :value="null" disabled v-if="field.placeholder">{{ field.placeholder }}</option>
                                      <option v-for="option in field.options" :key="option.value" :value="option.value">
                                          {{ option.text || option.label }} 
                                      </option>
                                  </select>
                              </template>

                               <!-- Radio Buttons -->
                               <template v-if="field.type === 'radio'">
                                  <label class="form-label small mb-1 d-block">{{ field.label }}</label>
                                  <div class="d-flex flex-wrap gap-3">
                                      <div v-for="option in field.options" :key="option.value" class="form-check form-check-inline">
                                           <input
                                              class="form-check-input"
                                              type="radio"
                                              :name="`${selectedCalculator.id}-${field.model}`"
                                              :id="`${selectedCalculator.id}-${field.model}-${option.value}`"
                                              :value="option.value"
                                              v-model="selectedCalculator.state[field.model]"
                                              :required="field.required !== false"
                                          >
                                          <label class="form-check-label small" :for="`${selectedCalculator.id}-${field.model}-${option.value}`">{{ option.text }}</label>
                                      </div>
                                  </div>
                               </template>

                               <!-- Checkbox (for single boolean) -->
                               <template v-if="field.type === 'checkbox'">
                                  <div class="form-check mt-2">
                                       <input
                                          class="form-check-input"
                                          type="checkbox"
                                          :id="`${selectedCalculator.id}-${field.model}`"
                                          v-model="selectedCalculator.state[field.model]"
                                       >
                                       <label class="form-check-label small" :for="`${selectedCalculator.id}-${field.model}`">{{ field.label }}</label>
                                  </div>
                               </template>

                          </div>
                      </div>
                  </div>
             </form>

             <!-- Result Display -->
              <div v-if="selectedCalculatorResult !== null" class="result-box mt-4 pt-3 pb-3"> 
                  Résultat : 
                  <strong class="fs-5"> 
                      {{ selectedCalculatorResult }}
                      <span v-if="selectedCalculator.resultLabel && typeof selectedCalculator.state.result !== 'object'">
                          {{ selectedCalculator.resultLabel }}
                      </span>
                  </strong>
              </div>
              <div v-else class="result-box mt-4 pt-3 pb-3 text-muted">
                    Entrez les valeurs pour calculer...
              </div>


              <!-- Interpretation Display -->
              <div v-if="interpretationText" class="interpretation-box alert alert-secondary small mt-3 p-2">
                  <strong>Interprétation :</strong> {{ interpretationText }}
              </div>

         </div> <!-- End card-body -->
      </section>
       <div v-else class="text-center p-5 text-muted border rounded bg-light">
           <p class="mb-0">Veuillez sélectionner un calculateur dans la liste ci-dessus<br>ou utiliser la barre de recherche.</p>
       </div>

    </div>
  </template>

  <script setup>
  import { ref, reactive, computed, watch } from 'vue';

  // --- Helper Function to deep copy initial state ---
  // Simple deep copy for plain objects/arrays/primitives, adjust if complex objects like Dates are needed
  const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));

  // --- Calculator Definitions ---
  // Store initial state separately for easy reset
  const calculatorDefinitions = [
      // Add many calculators here following the defined structure
      {
          id: 'cockcroft-gault', name: 'Clairance Créatinine (Cockcroft-Gault)', category: 'Fonction Rénale',
          keywords: ['rein', 'créat', 'crcl', 'clairance', 'cockcroft', 'gault', 'dfg', 'renal', 'clearance'],
          fields: [
              { model: 'age', label: 'Âge', type: 'number', unit: 'ans', placeholder: 'ans', required: true, colClass:'col-md-4', min: 0 },
              { model: 'weight', label: 'Poids', type: 'number', unit: 'kg', placeholder: 'kg', required: true, colClass:'col-md-4', min: 0 },
              { model: 'sex', label: 'Sexe', type: 'radio', options: [{value: 'male', text: 'H'}, {value: 'female', text: 'F'}], required: true, colClass:'col-md-4' },
              { model: 'serumCreatinine', label: 'Créatinine Sérique', type: 'number', step: 'any', placeholder: 'valeur', required: true, colClass:'col-md-8', min: 0 },
              { model: 'creatinineUnit', label: 'Unité Créat.', type: 'select', options: [{value: 'mg_dl', text: 'mg/dL'}, {value: 'umol_l', text: 'μmol/L'}], required: true, colClass:'col-md-4' },
          ],
          initialState: { age: null, weight: null, serumCreatinine: null, creatinineUnit: 'mg_dl', sex: 'male', result: null },
          calculate: (state) => {
              const { age, weight, serumCreatinine, creatinineUnit, sex } = state;
              if (age == null || weight == null || serumCreatinine == null || !creatinineUnit || !sex || age <= 0 || weight <= 0 || serumCreatinine <= 0) return null;

              const creatMgDl = creatinineUnit === 'umol_l' ? serumCreatinine / 88.4 : serumCreatinine;
              if (!creatMgDl || creatMgDl <= 0) return null; // Check after conversion

              let crcl = ((140 - age) * weight) / (72 * creatMgDl);
              if (sex === 'female') crcl *= 0.85;
              return crcl;
          },
          resultLabel: 'mL/min',
          resultPrecision: 1, // Specify precision
          interpretation: (r) => {
              if (r === null) return '';
              if (r < 15) return 'Stade 5: Insuffisance Rénale Terminale (< 15 mL/min)';
              if (r < 30) return 'Stade 4: Insuffisance Rénale Sévère (15-29 mL/min)';
              if (r < 60) return 'Stade 3: Insuffisance Rénale Modérée (30-59 mL/min)';
              if (r < 90) return 'Stade 2: Insuffisance Rénale Légère (60-89 mL/min)';
              return 'Stade 1: Fonction rénale normale ou DFG ≥ 90 mL/min (avec autres signes d\'atteinte rénale si applicable)';
          }
      },
      {
          id: 'kalemie-corrigee', name: 'Kaliémie Corrigée (pH)', category: 'Hydro-Électrolytique',
          keywords: ['potassium', 'k+', 'ph', 'acidose', 'alcalose', 'kaliémie', 'kalium'],
          fields: [
              { model: 'measuredK', label: 'K+ mesuré', type: 'number', step: 0.1, unit: 'mmol/L', placeholder: 'mmol/L', required: true, min: 0 },
              { model: 'measuredPh', label: 'pH mesuré', type: 'number', step: 0.01, placeholder: 'ex: 7.25', required: true, min: 6.5, max: 8.0 }, // Added realistic limits
          ],
          initialState: { measuredK: null, measuredPh: null, result: null },
          calculate: (state) => {
              const { measuredK, measuredPh } = state;
              // Check for valid number inputs
              if (measuredK == null || measuredPh == null || measuredK <= 0 || measuredPh <= 0) return null;
              if (measuredPh < 7.4) {
                    const phDifference = 7.4 - measuredPh;
                    // For each 0.1 decrease in pH below 7.4, K+ increases by ~0.6 mmol/L
                    const correctionFactor = (phDifference / 0.1) * 0.6;
                    return measuredK - correctionFactor; // Corrected K+ reflects value *at* pH 7.4
              }
              return measuredK; // No correction needed if pH >= 7.4
          },
          resultLabel: 'mmol/L (corrigé à pH 7.4)',
          resultPrecision: 1
      },
       {
          id: 'natremie-corrigee', name: 'Natrémie Corrigée (Glycémie)', category: 'Hydro-Électrolytique',
          keywords: ['sodium', 'na+', 'glycémie', 'glucose', 'hyperglycémie', 'natrémie', 'sucre'],
          fields: [
              { model: 'measuredNa', label: 'Na+ mesuré', type: 'number', unit: 'mmol/L', placeholder: 'mmol/L', required: true, min: 0 },
              { model: 'glucose', label: 'Glycémie', type: 'number', step: 'any', placeholder: 'valeur', required: true, colClass:'col-md-8', min: 0 },
              { model: 'glucoseUnit', label: 'Unité Glyc.', type: 'select', options: [{value: 'mg_dl', text: 'mg/dL'}, {value: 'mmol_l', text: 'mmol/L'}], required: true, colClass:'col-md-4' },
          ],
          initialState: { measuredNa: null, glucose: null, glucoseUnit: 'mg_dl', result: null },
          calculate: (state) => {
              const { measuredNa, glucose, glucoseUnit } = state;
               if (measuredNa == null || glucose == null || !glucoseUnit || measuredNa <= 0 || glucose < 0) return null; // Allow glucose = 0

              const glucoseMgDl = glucoseUnit === 'mmol_l' ? glucose * 18.0182 : glucose;
              let correctionFactor = 1.6;
              // Some sources use 2.4 when glucose > 400 mg/dL
              if (glucoseMgDl > 400) correctionFactor = 2.4;

              if (glucoseMgDl > 100) {
                  // For every 100 mg/dL glucose above 100, Na+ decreases by the correction factor
                  const correctedNa = measuredNa + correctionFactor * ((glucoseMgDl - 100) / 100);
                  return correctedNa;
              }
              return measuredNa; // No correction if glucose <= 100 mg/dL
          },
          resultLabel: 'mmol/L (corrigé)',
          resultPrecision: 1
      },
      {
          id: 'deficit-eau-libre', name: 'Déficit Eau Libre (Hypernatrémie)', category: 'Hydro-Électrolytique',
          keywords: ['eau', 'libre', 'déficit', 'hypernatrémie', 'hydratation', 'sodium', 'water', 'deficit'],
          fields: [
               { model: 'weight', label: 'Poids', type: 'number', unit: 'kg', placeholder: 'kg', required: true, colClass:'col-md-6', min: 0 },
               { model: 'sex', label: 'Sexe', type: 'radio', options: [{value: 'male', text: 'H'}, {value: 'female', text: 'F'}], required: true, colClass:'col-md-6' },
               { model: 'measuredNa', label: 'Na+ mesuré', type: 'number', unit: 'mmol/L', placeholder: 'mmol/L', required: true, colClass:'col-md-6', min: 0 },
               { model: 'targetNa', label: 'Na+ Cible', type: 'number', unit: 'mmol/L', placeholder:'(défaut 140)', required: false, colClass:'col-md-6', min: 0 }
          ],
          initialState: { weight: null, measuredNa: null, sex: 'male', targetNa: null, result: null }, // Target null initially
          calculate: (state) => {
              const { weight, measuredNa, sex } = state;
              const targetNaValue = state.targetNa || 140; // Use default if null or empty
              if (weight == null || measuredNa == null || !sex || weight <= 0 || measuredNa <= 0 || targetNaValue <= 0) return null;

              // Only calculate deficit if measured Na > target Na
              if (measuredNa <= targetNaValue) return 0; // No deficit if normo- or hyponatremic relative to target

              // Estimate Total Body Water (TBW) percentage based on sex (can be refined for age/obesity)
              const waterPercentage = sex === 'male' ? 0.6 : 0.5;
              const totalBodyWater = weight * waterPercentage;

              // Formula: TBW * ([Na+] / Target [Na+] - 1)
              return totalBodyWater * ((measuredNa / targetNaValue) - 1);
          },
          resultLabel: 'L',
          resultPrecision: 1,
          interpretation: (r) => {
              if (r === null) return '';
              if (r === 0) return 'Pas de déficit en eau libre calculé (Natrémie ≤ Cible).';
              if (r > 0) return `Déficit estimé. La correction doit être progressive (généralement sur 24-48h) pour éviter les oedèmes cérébraux. Ne pas dépasser une baisse de 8-12 mmol/L de Na+ par 24h.`;
              return '';
          }
      },
      {
          id: 'anion-gap', name: 'Trou Anionique', category: 'Gaz du Sang / Métabolique',
          keywords: ['trou', 'anionique', 'anion gap', 'acidose', 'métabolique', 'gds', 'bicarbonates', 'chlore', 'sodium', 'agap'],
          fields: [
               { model: 'sodium', label: 'Na+', type: 'number', unit: 'mmol/L', required: true, min: 0 },
               { model: 'chloride', label: 'Cl-', type: 'number', unit: 'mmol/L', required: true, min: 0 },
               { model: 'bicarbonate', label: 'HCO3-', type: 'number', unit: 'mmol/L', required: true, min: 0 },
          ],
          initialState: { sodium: null, chloride: null, bicarbonate: null, result: null },
          calculate: (state) => {
              const { sodium, chloride, bicarbonate } = state;
              // Check for valid number inputs
              if (sodium == null || chloride == null || bicarbonate == null || sodium <= 0 || chloride < 0 || bicarbonate < 0) return null;
              return sodium - (chloride + bicarbonate);
          },
          resultLabel: 'mmol/L',
          resultPrecision: 1,
          interpretation: (r) => {
              if (r === null) return '';
              // Normal range typically 8-12 mmol/L (can vary slightly by lab)
              if (r > 12) return 'Trou anionique élevé (> 12 mmol/L). Envisager MUDPILES (Méthanol, Urémie, Diabète (cétoacidose), Paraldéhyde/Phénformine, Isoniazide/Iron, Lactates, Éthylène glycol/Éthanol (cétoacidose), Salicylates).';
              if (r < 8) return 'Trou anionique bas (< 8 mmol/L). Envisager erreur de mesure, hypoalbuminémie, hypercalcémie/hypermagnesémie, myélome multiple, intoxication (bromide, lithium).';
              return 'Trou anionique normal (8-12 mmol/L). Si acidose métabolique présente, elle est probablement hyperchlorémique (envisager pertes digestives de HCO3-, pertes rénales, acidose tubulaire rénale, etc.).';
          }
      },
      {
          id: 'osmolarite-calculee', name: 'Osmolarité Calculée', category: 'Hydro-Électrolytique',
          keywords: ['osmolarité', 'osmolalité', 'sodium', 'urée', 'bun', 'glucose', 'glycémie', 'trou osmotique', 'osmotic gap'],
          fields: [
              { model: 'sodium', label: 'Na+', type: 'number', unit: 'mmol/L', required: true, colClass:'col-md-6', min: 0 },
              { model: 'glucose', label: 'Glycémie', type: 'number', step: 'any', placeholder: 'valeur', required: true, colClass:'col-md-8', min: 0 },
              { model: 'glucoseUnit', label: 'Unité Glyc.', type: 'select', options: [{value: 'mmol_l', text: 'mmol/L'}, {value: 'mg_dl', text: 'mg/dL'}], required: true, colClass:'col-md-4' },
              { model: 'urea', label: 'Urée ou BUN', type: 'number', step: 'any', placeholder: 'valeur', required: true, colClass:'col-md-8', min: 0 },
              { model: 'ureaUnit', label: 'Unité (Urée/BUN)', type: 'select', options: [{value: 'mmol_l', text: 'Urée (mmol/L)'}, {value: 'mg_dl', text: 'BUN (mg/dL)'}], required: true, colClass:'col-md-4' },
          ],
          initialState: { sodium: null, glucose: null, glucoseUnit: 'mmol_l', urea: null, ureaUnit:'mmol_l', result: null },
          calculate: (state) => {
              const { sodium, glucose, glucoseUnit, urea, ureaUnit } = state;
               if (sodium == null || glucose == null || !glucoseUnit || urea == null || !ureaUnit || sodium <= 0 || glucose < 0 || urea < 0) return null;

              const glucoseMmolL = glucoseUnit === 'mg_dl' ? glucose / 18.0182 : glucose;
               // BUN (Blood Urea Nitrogen) contains only the nitrogen component of urea. Urea = BUN * 2.14
               // Factor to convert BUN (mg/dL) to Urea (mmol/L) = (1 / 14 * 2) * 10 / 1 = 1 / 28 * 10 = 0.357 NO! -> (1 / (14*2)) * 1000 / 100 = 10/28 = 0.357 is wrong
               // BUN (mg/dL) * (10 dL/L) / (28 g/mol N in Urea) -> This gives mmol N / L, not Urea
               // BUN (mg/dL) to Urea (mmol/L): BUN * (1 / 2.14) -> Urea mg/dL. Urea mg/dL / 60.06 (MW Urea) * 10 dL/L -> Urea mmol/L
               // BUN (mg/dL) / 2.8 = Urea (mmol/L) approximately.  (Because Urea MW ~60, N MW 14, 2 N in urea -> 28/60 ~ 0.46. 1 / 0.46 / 10 = 2.14? No.. )
               // Correct: Urea (mmol/L) = BUN (mg/dL) / 2.8
               // Correct: Urea (mmol/L) = Urea (mg/dL) / 6.0
               const ureaMmolL = ureaUnit === 'mg_dl' /* BUN */ ? urea / 2.8 : urea /* Urea mmol/L */;

               if (glucoseMmolL < 0 || ureaMmolL < 0) return null; // Sanity check after conversion

              // Formula: 2 * Na+ + Glucose (mmol/L) + Urea (mmol/L)
              return (2 * sodium) + glucoseMmolL + ureaMmolL;
          },
          resultLabel: 'mOsm/kg H2O',
          resultPrecision: 0,
          interpretation: (r) => {
              if (r === null) return '';
              let text = `Osmolarité calculée : ${r.toFixed(0)} mOsm/kg. Plage normale approx. 275-295 mOsm/kg. `;
              if (r < 275) text += 'Indique une hypo-osmolarité. ';
              if (r > 295) text += 'Indique une hyper-osmolarité. ';
              text += 'Comparer à l\'osmolarité mesurée (si disponible) pour calculer le trou osmolaire (Osm Mesurée - Osm Calculée). Un trou > 10-15 mOsm/kg suggère la présence d\'osmolytes non mesurés (ex: éthanol, méthanol, éthylène glycol, mannitol).';
              return text;
          }
      },
      {
        id: 'dysnatremie-correction', name: 'Correction Natrémie (Adrogue-Madias)', category: 'Hydro-Électrolytique',
        keywords: ['sodium', 'natrémie', 'hypernatrémie', 'hyponatrémie', 'correction', 'adrogue', 'madias', 'fluid', 'perfusion'],
        fields: [
            { model: 'naSerum', label: 'Natrémie actuelle', type: 'number', step: 'any', unit: 'mmol/L', required: true, min: 0 },
            { model: 'naInfusate', label: '[Na+ + K+] du soluté', type: 'number', step: 'any', unit: 'mmol/L', required: true, min: 0, placeholder: 'ex: 154 pour NaCl 0.9%' },
            // Simplified: User enters infusate Na+ + K+ directly. Removed the select list for simplicity & flexibility.
            // { model: 'solute', label: 'Soluté utilisé', type: 'select', options: [ ... ], required: true }, // Removed
            // { model: 'naInfusateManual', label: '[Na+ + K+] du soluté (si "Autre")', type: 'number', step: 'any', unit: 'mmol/L', required: false, condition: (state) => state.solute === null }, // Removed
            { model: 'weight', label: 'Poids', type: 'number', unit: 'kg', required: true, min: 0 },
            { model: 'sex', label: 'Sexe', type: 'radio', options: [{ value: 'male', text: 'H' }, { value: 'female', text: 'F' }], required: true, colClass: 'col-md-6' }, // Added Sex for TBW
            // Optional: Target Na+ can be added if needed for specific goal calculations
        ],
        initialState: { naSerum: null, naInfusate: null, weight: null, sex: 'male', result: null },
        calculate: (state) => {
            const { naSerum, naInfusate, weight, sex } = state;
            if (naSerum == null || naInfusate == null || weight == null || !sex || naSerum <= 0 || naInfusate < 0 || weight <= 0) return null;

            // Estimate Total Body Water (TBW)
            const waterPercentage = sex === 'male' ? 0.6 : 0.5; // Could refine further (elderly ~0.45-0.5)
            const totalBodyWater = weight * waterPercentage;

            // Adrogue-Madias Formula: Change in Serum Na+ for 1L of infusate
            // ΔNa+ = ([Na+]inf + [K+]inf - [Na+]serum) / (TBW + 1)
            // Here, naInfusate represents [Na+]inf + [K+]inf
            const deltaNaPerLiter = (naInfusate - naSerum) / (totalBodyWater + 1);

            return deltaNaPerLiter; // Returns the expected change in serum Na+ (mmol/L) after infusing 1 Liter of the specified solution
        },
        resultLabel: 'mmol/L (Δ Na+ par Litre de soluté)',
        resultPrecision: 1,
        interpretation: (r) => {
            if (r === null) return '';
            let text = `L'infusion de 1 Litre de ce soluté devrait modifier la natrémie d'environ ${r.toFixed(1)} mmol/L. `;
            if (r > 0) text += `Utile pour corriger une hyponatrémie. `;
            if (r < 0) text += `Utile pour corriger une hypernatrémie. `;
            text += `La vitesse de correction est cruciale:
            - Hyponatrémie: Ne pas dépasser 8-10 mmol/L par 24h (risque de myélinolyse centropontine). Correction initiale plus rapide (ex: 1-2 mmol/L/h pendant 2-3h) si symptômes sévères.
            - Hypernatrémie: Ne pas dépasser 10-12 mmol/L par 24h (risque d'oedème cérébral).
            Utilisez ce résultat pour déterminer le débit de perfusion nécessaire pour atteindre la vitesse de correction souhaitée. Ex: Pour corriger de 0.5 mmol/L/h avec un ΔNa+/L de ${r.toFixed(1)}, le débit requis est (0.5 / ${Math.abs(r).toFixed(1)}) L/h = ${(0.5 / Math.abs(r) * 1000).toFixed(0)} mL/h (si r != 0).`;
            return text;
        }
    },
    {
        id: 'debit-catecholamines', name: 'Débit Perfusionnel Catécholamines', category: 'Anesthésie / Réanimation',
        keywords: ['perfusion', 'catécholamines', 'noradrénaline', 'adrénaline', 'débit', 'ml/h', 'vasopresseur', 'rate', 'infusion'],
        fields: [
            { model: 'dose', label: 'Dose cible', type: 'number', step: 'any', unit: 'µg/kg/min', required: true, min: 0 },
            { model: 'weight', label: 'Poids', type: 'number', unit: 'kg', required: true, min: 0 },
            { model: 'concentration', label: 'Concentration Dilution', type: 'number', step: 'any', unit: 'mg / mL', placeholder: 'mg dans X mL', required: true, min: 0, colClass: 'col-md-6' },
            { model: 'dilutionVolume', label: 'Volume Dilution', type: 'number', step: 'any', unit: 'mL', placeholder: 'Volume total (ex: 50)', required: true, min: 0, colClass: 'col-md-6' }
        ],
        initialState: { dose: null, weight: null, concentration: null, dilutionVolume: null, result: null },
        calculate: (state) => {
            const { dose, weight, concentration, dilutionVolume } = state;
            if (dose == null || weight == null || concentration == null || dilutionVolume == null || dose < 0 || weight <= 0 || concentration <= 0 || dilutionVolume <= 0) return null;

            // Concentration in µg/mL = (Concentration (mg) * 1000) / Volume (mL)
            const concentrationUgPerMl = (concentration * 1000) / dilutionVolume;
            if (concentrationUgPerMl <= 0) return null;

            // Total dose needed in µg/min = dose (µg/kg/min) * weight (kg)
            const totalUgMin = dose * weight;

            // Rate (mL/h) = (Total µg/min * 60 min/h) / Concentration (µg/mL)
            return (totalUgMin * 60) / concentrationUgPerMl;
        },
        resultLabel: 'mL/h',
        resultPrecision: 1
    },
    { // Simplified version if concentration is known directly in ug/mL
      id: 'debit-catecholamines-simple', name: 'Débit Perfusionnel (Conc. µg/mL)', category: 'Anesthésie / Réanimation',
      keywords: ['perfusion', 'catécholamines', 'noradrénaline', 'adrénaline', 'débit', 'ml/h', 'vasopresseur', 'rate', 'infusion', 'simple'],
      fields: [
          { model: 'dose', label: 'Dose cible', type: 'number', step: 'any', unit: 'µg/kg/min', required: true, min: 0 },
          { model: 'weight', label: 'Poids', type: 'number', unit: 'kg', required: true, min: 0 },
          { model: 'concentrationUgMl', label: 'Concentration', type: 'number', step: 'any', unit: 'µg/mL', required: true, min: 0 },
      ],
      initialState: { dose: null, weight: null, concentrationUgMl: null, result: null },
      calculate: (state) => {
          const { dose, weight, concentrationUgMl } = state;
          if (dose == null || weight == null || concentrationUgMl == null || dose < 0 || weight <= 0 || concentrationUgMl <= 0) return null;
          const totalUgMin = dose * weight;
          return (totalUgMin * 60) / concentrationUgMl; // mL/h
      },
      resultLabel: 'mL/h',
      resultPrecision: 1
    },
    // Note: O2 target calculation is highly variable and depends on delivery device.
    // This is a *very rough* estimate for nasal cannula only.
    {
      id: 'o2-target-nc', name: 'O2 Nasal (SpO2 Cible) - Estimation', category: 'Oxygénothérapie',
      keywords: ['oxygène', 'spo2', 'o2', 'saturation', 'débit', 'lunettes', 'nasal cannula'],
      fields: [
          { model: 'currentSpO2', label: 'SpO2 actuelle', type: 'number', unit: '%', required: true, min: 0, max: 100 },
          { model: 'targetSpO2', label: 'SpO2 cible', type: 'number', unit: '%', required: true, min: 0, max: 100 },
          { model: 'currentFiO2', label: 'FiO2 actuelle estimée', type: 'number', unit: '%', placeholder: '21% (air ambiant)', required: false, min:21, max:100 },
      ],
      initialState: { currentSpO2: null, targetSpO2: null, currentFiO2: 21, result: null },
      calculate: (state) => {
          const { currentSpO2, targetSpO2 } = state;
          if (currentSpO2 == null || targetSpO2 == null || currentSpO2 < 0 || targetSpO2 <= 0 || currentSpO2 > 100 || targetSpO2 > 100 ) return null;

          if (targetSpO2 <= currentSpO2) return 0; // No increase needed

          // VERY ROUGH RULE OF THUMB for Nasal Cannula:
          // Each L/min adds approx 3-4% FiO2 above air (21%)
          // FiO2 ≈ 21% + (Flow Rate * 4%)
          // We want to find Flow Rate needed.
          // This requires estimating target FiO2 from target SpO2, which is non-linear (Oxyhemoglobin curve)
          // Let's use a simpler approach: Increase flow by 1L for every ~3-4% SpO2 deficit.

          const deficit = targetSpO2 - currentSpO2;
          const estimatedFlow = Math.ceil(deficit / 3.5); // Average of 3-4

          // Limit flow (nasal cannula usually max 6L/min, higher needs different device)
          return Math.min(estimatedFlow, 6);
      },
      resultLabel: 'L/min (via lunettes nasales)',
      resultPrecision: 0,
      interpretation: (r) => {
          if (r === null) return '';
          if (r === 0) return 'La SpO2 cible est atteinte ou dépassée. Pas d\'augmentation de débit O2 nécessaire via lunettes.';
          let text = `Débit estimé via lunettes nasales: ${r} L/min. Ceci est une estimation très approximative. La réponse individuelle varie. Titrer le débit en fonction de la SpO2 réelle obtenue. `;
          if (r >= 6) {
             text += 'Si la cible n\'est pas atteinte à 6 L/min, envisager un autre dispositif (masque simple, masque haute concentration, VNI...).';
          }
          text += ' Tenir compte des objectifs de SpO2 spécifiques à la pathologie (ex: 88-92% pour BPCO hypercapnique).';
          return text;
      }
  },
  {
        id: 'nihss-score-input',
        name: 'NIHSS Score (Entrée Directe)',
        category: 'Neurologie / AVC',
        keywords: ['nihss', 'stroke', 'avc', 'score', 'neurologie', 'neuro'],
        fields: [
            { model: 'totalScore', label: 'Score NIHSS Total', type: 'number', required: true, min: 0, max: 42, placeholder: 'Entrez le score (0-42)' },
        ],
        initialState: { totalScore: null, result: null },
        calculate: (state) => {
            // Directly returns the entered score
            const { totalScore } = state;
            if (totalScore != null && totalScore >= 0 && totalScore <= 42) {
                return totalScore;
            }
            return null;
        },
        resultLabel: 'points',
        resultPrecision: 0,
        interpretation: (r) => {
            if (r === null) return 'Entrez le score NIHSS total.';
            let interp = `Score NIHSS: ${r}. Interprétation indicative de la sévérité de l'AVC:\n`;
            if (r === 0) return interp + '- 0: Pas de déficit apparent.';
            if (r >= 1 && r <= 4) return interp + '- 1-4: Déficit mineur.';
            if (r >= 5 && r <= 15) return interp + '- 5-15: Déficit modéré.';
            if (r >= 16 && r <= 20) return interp + '- 16-20: Déficit modéré à sévère.';
            if (r >= 21) return interp + '- 21-42: Déficit sévère.';
            return interp; // Should not be reached if r is valid
        }
    },    {
        id: 'abcd2-score',
        name: 'Score ABCD²',
        category: 'Neurologie / AVC',
        keywords: ['abcd2', 'tia', 'ait', 'avc', 'stroke', 'risk', 'risque', 'transient ischemic attack', 'accident ischémique transitoire'],
        fields: [
            {
                model: 'age', label: 'Âge ≥ 60 ans', type: 'radio', options: [
                    { value: 1, text: 'Oui (≥ 60 ans)' }, // 1 point
                    { value: 0, text: 'Non (< 60 ans)' }  // 0 points
                ], required: true, colClass:'col-md-6'
            },
            {
                model: 'bloodPressure', label: 'Pression Artérielle ≥ 140/90 mmHg', type: 'radio', options: [
                    { value: 1, text: 'Oui (PAS ≥ 140 ou PAD ≥ 90)' }, // 1 point
                    { value: 0, text: 'Non (< 140/90)' }            // 0 points
                ], required: true, colClass:'col-md-6'
            },
            {
                model: 'clinicalFeatures', label: 'Caractéristiques Cliniques', type: 'radio', options: [
                    { value: 2, text: 'Déficit moteur unilatéral' },             // 2 points
                    { value: 1, text: 'Trouble de la parole sans déficit moteur' }, // 1 point
                    { value: 0, text: 'Autres symptômes' }                      // 0 points
                ], required: true, colClass:'col-12' // Takes more space
            },
            {
                model: 'duration', label: 'Durée des Symptômes', type: 'radio', options: [
                    { value: 2, text: '≥ 60 minutes' },      // 2 points
                    { value: 1, text: '10-59 minutes' },     // 1 point
                    { value: 0, text: '< 10 minutes' }       // 0 points
                ], required: true, colClass:'col-md-6'
            },
             {
                model: 'diabetes', label: 'Diabète', type: 'radio', options: [
                    { value: 1, text: 'Oui' },             // 1 point
                    { value: 0, text: 'Non' }              // 0 points
                ], required: true, colClass:'col-md-6'
            },
        ],
        // Initial state - ensure all models exist and have a default (null or 0 for points)
        initialState: { age: null, bloodPressure: null, clinicalFeatures: null, duration: null, diabetes: null, result: null },
        calculate: (state) => {
            const { age, bloodPressure, clinicalFeatures, duration, diabetes } = state;
            // Check if all required fields have been selected (are not null)
            if (age === null || bloodPressure === null || clinicalFeatures === null || duration === null || diabetes === null) {
                return null;
            }
            // Sum the points from each selection
            return age + bloodPressure + clinicalFeatures + duration + diabetes;
        },
        resultLabel: 'points',
        resultPrecision: 0,
        interpretation: (r) => {
            if (r === null) return 'Veuillez remplir tous les champs pour calculer le score.';
            let interp = `Score ABCD²: ${r}. Estimation du risque d'AVC à court terme (2 jours / 7 jours / 90 jours) après un AIT:\n`;
            if (r >= 0 && r <= 3) return interp + `- Score 0-3 (Faible risque): ~1% / 1.2% / 3.1%`;
            if (r >= 4 && r <= 5) return interp + `- Score 4-5 (Risque modéré): ~4.1% / 5.9% / 9.8%`;
            if (r >= 6 && r <= 7) return interp + `- Score 6-7 (Risque élevé): ~8.1% / 11.7% / 17.8%`;
            return interp + "- Score invalide."; // Should not happen with validation
            // Source des risques approx: Johnston SC, et al. Lancet. 2007;369(9558):283-292. (Risques peuvent varier légèrement selon les études)
            // Note importante: Ce score est une aide à la décision et ne remplace pas le jugement clinique. Une évaluation urgente est souvent recommandée même pour les scores faibles/modérés.
        }
    },
  {
      id: 'gds-anion-gap-combined', // Renamed to avoid conflict
      name: 'Interprétation Acido-Basique (GDS + TA)',
      category: 'Gaz du Sang / Métabolique',
      keywords: ['gaz du sang', 'trou anionique', 'anion gap', 'acidose', 'alcalose', 'métabolique', 'respiratoire', 'delta gap', 'bicarbonate', 'ph', 'paco2', 'hco3', 'base excess', 'be', 'gds', 'abg'],
      fields: [
          { model: 'ph', label: 'pH', type: 'number', step: 0.001, required: true, min: 6.5, max: 8.0 },
          { model: 'pco2', label: 'PaCO2', type: 'number', step: 'any', unit: 'mmHg', required: true, min: 5, max: 150 },
          { model: 'hco3', label: 'HCO3- (Bicarbonate)', type: 'number', step: 'any', unit: 'mmol/L', required: true, min: 0 },
          { model: 'na', label: 'Na+', type: 'number', step: 'any', unit: 'mmol/L', required: true, min: 0 },
          { model: 'cl', label: 'Cl-', type: 'number', step: 'any', unit: 'mmol/L', required: true, min: 0 },
          // Optional fields for more complex interpretation
          { model: 'k', label: 'K+ (optionnel)', type: 'number', step: 'any', unit: 'mmol/L', required: false, min: 0 },
          { model: 'albumin', label: 'Albumine (g/dL, optionnel)', type: 'number', step: 'any', unit: 'g/dL', required: false, min: 0 },
      ],
      initialState: { ph: null, pco2: null, hco3: null, na: null, cl: null, k: null, albumin: null, result: null },
      calculate: (state) => {
          const { ph, pco2, hco3, na, cl, albumin } = state;
          // Basic required fields check
          if (ph == null || pco2 == null || hco3 == null || na == null || cl == null ||
              ph <= 0 || pco2 <= 0 || hco3 < 0 || na <= 0 || cl < 0) { // Allow HCO3=0
              return null;
          }

          // --- Step 1: Acidemia or Alkalemia? ---
          let primaryDisorder = '';
          if (ph < 7.35) primaryDisorder = 'Acidose';
          else if (ph > 7.45) primaryDisorder = 'Alcalose';
          else primaryDisorder = 'pH normal'; // Could be compensated or mixed

          // --- Step 2: Respiratory or Metabolic? ---
          let respiratoryComponent = '';
          let metabolicComponent = '';
          // Respiratory: Check PaCO2 (Normal ~35-45 mmHg)
          if (pco2 > 45) respiratoryComponent = 'Respiratoire (hypercapnie)';
          else if (pco2 < 35) respiratoryComponent = 'Respiratoire (hypocapnie)';
          // Metabolic: Check HCO3- (Normal ~22-26 mmol/L)
          if (hco3 < 22) metabolicComponent = 'Métabolique (base faible)';
          else if (hco3 > 26) metabolicComponent = 'Métabolique (base élevée)';

          // Determine Primary Driver based on pH
          let primaryDiagnosis = '';
          if (primaryDisorder === 'Acidose') {
              if (respiratoryComponent.includes('hypercapnie')) primaryDiagnosis = 'Acidose Respiratoire';
              else if (metabolicComponent.includes('base faible')) primaryDiagnosis = 'Acidose Métabolique';
              else primaryDiagnosis = 'Acidose (origine indéterminée sans compensation claire)'; // Should be rare if data valid
          } else if (primaryDisorder === 'Alcalose') {
              if (respiratoryComponent.includes('hypocapnie')) primaryDiagnosis = 'Alcalose Respiratoire';
              else if (metabolicComponent.includes('base élevée')) primaryDiagnosis = 'Alcalose Métabolique';
              else primaryDiagnosis = 'Alcalose (origine indéterminée sans compensation claire)';
          } else { // pH Normal
              if (respiratoryComponent && metabolicComponent) primaryDiagnosis = 'Trouble Mixte ou Compensé';
              else if (respiratoryComponent) primaryDiagnosis = `pH Normal avec ${respiratoryComponent}`;
              else if (metabolicComponent) primaryDiagnosis = `pH Normal avec ${metabolicComponent}`;
              else primaryDiagnosis = 'Gaz du sang normaux';
          }

          // --- Step 3: Anion Gap (for Metabolic Acidosis) ---
          let anionGap = na - (cl + hco3);
          let anionGapCorrected = anionGap; // Initialize corrected AG
          let agInterpretation = `Trou Anionique (TA) = ${anionGap.toFixed(1)} mmol/L. `;

          if (albumin != null && albumin > 0 && albumin < 4.0) { // Correct AG if albumin is low (Normal ~4.0 g/dL)
               // Correction: Add 2.5 mmol/L to AG for every 1 g/dL decrease in albumin below 4.0
               const albuminCorrection = (4.0 - albumin) * 2.5;
               anionGapCorrected = anionGap + albuminCorrection;
               agInterpretation += `TA corrigé pour albumine (${albumin.toFixed(1)} g/dL) = ${anionGapCorrected.toFixed(1)} mmol/L. `;
          } else {
               agInterpretation += `(Albumine non fournie ou normale, TA non corrigé). `;
          }

          const relevantAG = anionGapCorrected; // Use corrected AG if available
          if (relevantAG > 12) agInterpretation += 'TA élevé. ';
          else if (relevantAG < 8) agInterpretation += 'TA bas. ';
          else agInterpretation += 'TA normal. ';

          // --- Step 4: Delta-Delta Ratio (if High AG Metabolic Acidosis) ---
          let deltaRatioInterpretation = '';
          if (primaryDiagnosis === 'Acidose Métabolique' && relevantAG > 12) {
              const deltaAG = relevantAG - 12; // Change in AG from normal midpoint
              const deltaHCO3 = 24 - hco3; // Change in HCO3 from normal midpoint
              if (deltaHCO3 !== 0) { // Avoid division by zero
                  const deltaRatio = deltaAG / deltaHCO3;
                  deltaRatioInterpretation = `Delta Ratio (ΔTA / ΔHCO3) = ${deltaRatio.toFixed(1)}. `;
                  if (deltaRatio < 0.8) deltaRatioInterpretation += 'Suggère une acidose hyperchlorémique associée (pertes de HCO3- > gain d\'acides non mesurés).';
                  else if (deltaRatio >= 0.8 && deltaRatio <= 1.2) deltaRatioInterpretation += 'Suggère une acidose métabolique à TA élevé "pure".'; // Narrowed range
                  else deltaRatioInterpretation += 'Suggère une alcalose métabolique associée (gain d\'acides < perte de HCO3-) ou une acidose respiratoire chronique préexistante.';
              } else {
                   deltaRatioInterpretation = 'Delta Ratio non calculable (ΔHCO3 = 0). ';
              }
          }

          // --- Step 5: Compensation Assessment (Simplified) ---
          // TODO: Add expected compensation formulas (e.g., Winter's for Met Acidosis)
          let compensationInterpretation = ''; // Placeholder

          // --- Construct Final Interpretation ---
          let fullInterpretation = `1. État acido-basique: ${primaryDisorder}.
2. Trouble primaire probable: ${primaryDiagnosis}.
3. Analyse du Trou Anionique: ${agInterpretation}
4. Analyse Delta Ratio (si applicable): ${deltaRatioInterpretation}
5. Compensation: ${compensationInterpretation || 'Non évaluée dans cette version simplifiée.'}`;

          return {
              primaryDiagnosis: primaryDiagnosis,
              anionGap: anionGap.toFixed(1),
              anionGapCorrected: (albumin != null && albumin > 0 && albumin < 4.0) ? anionGapCorrected.toFixed(1) : null,
              fullInterpretation: fullInterpretation.replace(/\n/g, ' ') // Return as single line for display
          };
      },
      // Result is an object, handle display in computed properties
      resultLabel: 'Interprétation', // Generic label
      resultPrecision: 1 // For AG display
  },
      // ... add more calculators ...
  ];

  // Create reactive state for each calculator based on its initial state
  const allCalculators = reactive(calculatorDefinitions.map(def => ({
      ...def,
      state: reactive(deepCopy(def.initialState)), // Each calculator manages its own reactive state
      initialState: deepCopy(def.initialState) // Keep a non-reactive copy of initial state for resets
  })));

  // --- State for Selection and Search ---
  const searchTerm = ref('');
  const selectedCalculatorId = ref(null); // Holds the ID

  // --- Computed Properties ---
  const availableCalculatorOptions = computed(() => {
      const lowerSearch = searchTerm.value.toLowerCase().trim();
      if (!lowerSearch) {
          return allCalculators; // Return full objects if no search
      }
      return allCalculators.filter(calc =>
          calc.name.toLowerCase().includes(lowerSearch) ||
          calc.category.toLowerCase().includes(lowerSearch) ||
          calc.keywords?.some(k => k.toLowerCase().includes(lowerSearch))
      );
  });

  const groupedCalculatorOptions = computed(() => {
      // Use availableCalculatorOptions which respects the search term
      return availableCalculatorOptions.value.reduce((acc, calc) => {
          const category = calc.category || 'Autres';
          if (!acc[category]) acc[category] = [];
          // Push only id and name for the select options
          acc[category].push({ id: calc.id, name: calc.name });
          return acc;
      }, {});
  });


  // The currently selected full calculator reactive object
  const selectedCalculator = computed(() => {
      if (!selectedCalculatorId.value) return null;
      // Find the calculator in the reactive 'allCalculators' array
      return allCalculators.find(calc => calc.id === selectedCalculatorId.value);
  });

  // Formatted result of the selected calculator
  const selectedCalculatorResult = computed(() => {
      if (!selectedCalculator.value || selectedCalculator.value.state.result === null) return null;

      const result = selectedCalculator.value.state.result;
      const precision = selectedCalculator.value.resultPrecision ?? 1; // Default precision

      // Handle object results (like from GDS interpretation)
      if (typeof result === 'object' && result !== null) {
           // Customize display for specific object results
           if (selectedCalculator.value.id === 'gds-anion-gap-combined') {
               let display = `Diagnostic Primaire: ${result.primaryDiagnosis || 'N/A'}. TA: ${result.anionGap || 'N/A'}`;
               if(result.anionGapCorrected) display += ` (corrigé: ${result.anionGapCorrected})`;
               return display;
           }
           // Default object display (can be improved)
           return JSON.stringify(result);
      }

       // Handle number results
       if (typeof result === 'number') {
             return result.toFixed(precision);
       }

      // Handle string results (or others) directly
      return result;
  });

  // Interpretation text for the selected calculator's result
  const interpretationText = computed(() => {
      if (!selectedCalculator.value || selectedCalculator.value.state.result === null) return '';

      const result = selectedCalculator.value.state.result;

      // If result is the GDS object, extract its interpretation
      if (selectedCalculator.value.id === 'gds-anion-gap-combined' && typeof result === 'object' && result?.fullInterpretation) {
          return result.fullInterpretation;
      }

      // Use the standard interpretation function if it exists
      if (selectedCalculator.value.interpretation) {
         return selectedCalculator.value.interpretation(result);
      }

      return ''; // No interpretation available
  });


  // --- Watchers ---
  // Watch the state object of the currently selected calculator for changes
  watch(() => selectedCalculator.value?.state,
    (newState) => {
        // Ensure a calculator is selected and we have its definition
        if (selectedCalculator.value && newState) {
            const calculator = selectedCalculator.value; // Get the reactive calculator object
            // Recalculate using the calculator's own calculate function
            const newResult = calculator.calculate(newState);

            // Update the result in the calculator's reactive state
            // This check prevents infinite loops if calculate returns the exact same object/value
             if (JSON.stringify(calculator.state.result) !== JSON.stringify(newResult)) {
                 calculator.state.result = newResult;
                 // console.log(`Recalculated ${calculator.name}, result:`, newResult);
             }
        }
    },
    { deep: true } // Watch for nested changes within the state object
  );

  // Clear search term when a calculator is selected via dropdown
  watch(selectedCalculatorId, (newId) => {
      if (newId !== null) {
          searchTerm.value = ''; // Clear search on selection
      }
  });


  // --- Methods ---
  // Reset the calculator *selection*
  const resetCalculatorSelection = () => {
      selectedCalculatorId.value = null; // Clear selection
      searchTerm.value = ''; // Also clear search
  };

  // Reset ONLY the inputs and result of the *currently selected* calculator to their initial values
  const resetSpecificCalculatorInputs = () => {
      if (selectedCalculator.value) {
           const calc = selectedCalculator.value;
           // Overwrite the current state with a deep copy of the stored initial state
           Object.assign(calc.state, deepCopy(calc.initialState));
           console.log(`Reset inputs for ${calc.name}`);
      }
  };

  </script>

  <style scoped>
    /* Styles specific to Calculators view */
    .calculator-select-section {
        /* Use theme variable or fallback */
        background-color: var(--bs-light-bg-subtle, #f8f9fa); /* Adapts to Bootstrap light/dark */
        /* Consider removing sticky top if it causes issues, or ensure --header-height is defined */
        /* position: sticky;
        top: calc(var(--header-height, 55px) + 1px); */
        z-index: 90;
        padding: 1rem; /* Consistent padding */
    }

    .selected-calculator-section .card-header {
        /* Use theme variable or fallback */
        background-color: var(--bs-primary, #0d6efd);
        color: white;
        padding: 0.75rem 1rem;
    }
    .selected-calculator-section .card-header .h5 { color: white; } /* Ensure heading text is white */

    .selected-calculator-section .card-header .btn-outline-light {
        padding: 0.2rem 0.5rem;
        font-size: 0.8rem;
        border-width: 1px;
        color: rgba(255,255,255,0.8);
        border-color: rgba(255,255,255,0.5);
        vertical-align: middle; /* Align button nicely */
    }
    .selected-calculator-section .card-header .btn-outline-light:hover {
        background-color: rgba(255,255,255,0.1);
        border-color: #fff;
         color: #fff;
    }
    .selected-calculator-section .card-header .btn-outline-light svg {
        width: 0.8em; height: 0.8em; margin-right: 0.3em; margin-top: -2px; /* Adjust vertical alignment */
        vertical-align: middle;
    }

    .selected-calculator-section .card-body {
         padding: 1.25rem; /* Slightly more padding in body */
    }

    .result-box {
        /* Use theme variable or fallback */
        background-color: var(--bs-secondary-bg, #e9ecef);
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: var(--bs-border-radius, 0.375rem);
        padding: 0.75rem 1rem; /* Adjusted padding */
        margin-top: 1.5rem; /* More space above result */
        font-size: 1rem;
        text-align: center;
        word-wrap: break-word; /* Prevent overflow */
    }

    /* Specific styling for dark mode if using Bootstrap's dark theme */
    [data-bs-theme="dark"] .result-box {
        background-color: var(--bs-secondary-bg, #343a40); /* Adjust dark mode background */
        border-color: var(--bs-border-color-translucent, rgba(255, 255, 255, 0.15));
    }

    .result-box strong {
        /* Use theme variable or fallback */
        color: var(--bs-primary-text-emphasis, #052c65);
        font-weight: 600; /* Slightly bolder */
        /* font-size adjusted in template */
        display: inline-block;
        margin-left: 0.5ch;
    }
    [data-bs-theme="dark"] .result-box strong {
        color: var(--bs-primary-text-emphasis, #6ea8fe);
    }

    .interpretation-box {
        font-size: 0.875em;
        /* Use standard Bootstrap alert styling */
        /* background-color adjusted by alert-secondary */
        /* border-color adjusted by alert-secondary */
         color: var(--bs-secondary-text-emphasis);
         background-color: var(--bs-secondary-bg-subtle);
         border-color: var(--bs-secondary-border-subtle);
         white-space: pre-line; /* Respect line breaks in interpretation text */
    }

    .form-label.small {
        font-size: 0.85em;
        margin-bottom: 0.2rem;
        color: var(--bs-secondary-color, #6c757d); /* Use Bootstrap variable */
        font-weight: 500;
    }
    .form-select-sm, .input-group-sm > .form-control { /* Target input within group */
      font-size: 0.875rem;
    }
    .form-check-label.small { font-size: 0.875rem; }

    /* Reset button style */
    .btn-link.text-muted {
        text-decoration: none;
        font-size: 0.85em;
    }
     .btn-link.text-muted:hover {
        text-decoration: underline;
     }

  </style>